"use client";

import { AuthProvider } from "@/modules/auth/context/AuthContext";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import Navbar from "@/modules/web/components/Navbar";
import Footer from "@/modules/web/components/Footer";
import GoogleTagManager from "@/components/GoogleTagManager";
import MetaPixel from "@/components/MetaPixel";
import CampaignZone from "@/components/campaign/CampaignZone";

/**
 * Shell for the whole public site, including the booking/enrolment flow.
 *
 * Navbar and Footer live here rather than inside each page. Previously all 18
 * public pages rendered their own copy, so every navigation unmounted and
 * remounted the entire chrome - the header re-fetched the user, site settings
 * and category menu, lost its open/scroll state, and visibly flashed. Hoisting
 * them into the layout keeps them mounted across every route change in this
 * group, which is what makes navigation feel like a single-page app.
 *
 * The booking routes were merged into this group for the same reason: crossing
 * a route-group boundary remounts the layout, so a separate (booking) group
 * meant the chrome still flashed on the course -> enrol step. Route groups do
 * not affect URLs, so /[locale]/book/** is unchanged. Booking keeps its own
 * nested layout for the email-verification guard.
 */
export default function WebLayout({ children }: { children: React.ReactNode }) {
  const { user } = useCurrentUser();

  const content = (
    <>
      <GoogleTagManager />
      <MetaPixel />
      <Navbar />
      {/* Mounted once here (not per-page) so every public page inherits both
          zones automatically — mirrors the Student Dashboard layout's
          contentTop/contentBottom pattern, independent of dashboard_top/bottom. */}
      <CampaignZone name="site_top" />
      {children}
      <CampaignZone name="site_bottom" />
      <Footer />
    </>
  );

  if (user) {
    return <AuthProvider user={user}>{content}</AuthProvider>;
  }

  return content;
}
